// Per-fact practice stats + session bests, persisted to first-party
// localStorage in the ops.ts mold: pure, tested update/query functions with
// thin best-effort load/save wrappers. This is the shape a future account
// backend syncs — design it here, and accounts become a sync problem.
import type { Op } from "./problem";

export interface FactStat {
  /** attempts */
  n: number;
  /** correct answers */
  right: number;
  /** exponential moving average of response time (ms) */
  msEma: number;
}

export interface Stats {
  facts: Record<string, FactStat>;
  bestScore: number;
  bestStreak: number;
}

export function emptyStats(): Stats {
  return { facts: {}, bestScore: 0, bestStreak: 0 };
}

interface FactLike {
  a: number;
  b: number;
  op: Op;
}

/** One key per *fact*: commutative ops (+ ×) normalize operand order. */
export function factKey(p: FactLike): string {
  const commutative = p.op === "+" || p.op === "*";
  const [x, y] = commutative && p.b < p.a ? [p.b, p.a] : [p.a, p.b];
  return `${x}${p.op}${y}`;
}

// EMA weight for the newest sample — heavy enough to follow a kid's real
// progress within a session, light enough to shrug off one distracted answer.
const EMA_ALPHA = 0.3;

// Cap a single sample so one walked-away round can't poison the average.
const MAX_SAMPLE_MS = 15_000;

export function recordAttempt(stats: Stats, p: FactLike, correct: boolean, ms: number): void {
  ms = Math.min(ms, MAX_SAMPLE_MS);
  const key = factKey(p);
  const f = stats.facts[key];
  if (!f) {
    stats.facts[key] = { n: 1, right: correct ? 1 : 0, msEma: ms };
    return;
  }
  f.n += 1;
  if (correct) f.right += 1;
  f.msEma = f.msEma + (ms - f.msEma) * EMA_ALPHA;
}

export function recordSession(stats: Stats, score: number, streak: number): void {
  stats.bestScore = Math.max(stats.bestScore, score);
  stats.bestStreak = Math.max(stats.bestStreak, streak);
}

// Weakness weights accuracy over speed; unseen facts sit above mastered ones
// so new material keeps entering the rotation.
const UNSEEN_WEAKNESS = 0.55;
const SLOW_MS = 4000; // an EMA this slow counts as maximally "slow"

export function weakness(stats: Stats, p: FactLike): number {
  const f = stats.facts[factKey(p)];
  if (!f || f.n === 0) return UNSEEN_WEAKNESS;
  const accuracy = f.right / f.n;
  const slowness = Math.max(0, Math.min(1, f.msEma / SLOW_MS));
  return (1 - accuracy) * 0.7 + slowness * 0.3;
}

/** Of several candidate problems, the one most in need of practice (ties → first). */
export function pickWeakest<T extends FactLike>(candidates: T[], stats: Stats): T {
  let best = candidates[0]!;
  let bestW = -1;
  for (const c of candidates) {
    const w = weakness(stats, c);
    if (w > bestW) {
      best = c;
      bestW = w;
    }
  }
  return best;
}

// ── persistence (best-effort, first-party localStorage only) ────────────────
const STORE_KEY = "gms.stats";

export function loadStats(): Stats {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<Stats>;
      return {
        facts: parsed.facts && typeof parsed.facts === "object" ? parsed.facts : {},
        bestScore: typeof parsed.bestScore === "number" ? parsed.bestScore : 0,
        bestStreak: typeof parsed.bestStreak === "number" ? parsed.bestStreak : 0,
      };
    }
  } catch {
    /* storage unavailable / malformed — fall through to fresh stats */
  }
  return emptyStats();
}

export function saveStats(stats: Stats): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(stats));
  } catch {
    /* persistence is best-effort */
  }
}
